import React from 'react';

import { format, parseISO } from 'date-fns';
import { isBoolean } from 'lodash-es';
import { useParams } from 'react-router-dom';

import ErrorPage from '@/components/errors/ErrorPage';
import SingleColumnTemplate from '@/components/layouts/SingleColumnTemplate';
import UiStackTemplate from '@/components/layouts/UiStackTemplate';
import Jumbotron from '@/components/ui/box/Jumbotron';
import Panel from '@/components/ui/box/Panel';
import Paper from '@/components/ui/box/Paper';
import Button from '@/components/ui/buttons/Button';
import Field from '@/components/ui/forms/Field';
import FormGroup from '@/components/ui/forms/FormGroup';
import PageTitle from '@/components/ui/headers/PageTitle';
import useFetch from '@/hooks/common/useFetch';
import useModal from '@/hooks/common/useModal';
import useReactHookForm from '@/hooks/common/useReactHookForm';
import useCurrentUser from '@/hooks/store/useCurrentUser';

import AbsencesConfirmPage from './AbsencesConfirmPage';
import { type AbsenceDetailPayload } from './AbsencesDetailPage';
import styles from './AbsencesEditPage.module.scss';
import { contactTypeNames } from './constants';
import AbsencesFormInputsOnOrAfterReason from './form/AbsencesFormInputsOnOrAfterReason';
import AbsencesFormStaticInput from './form/AbsencesFormStaticInput';
import {
  convertToSubmitValuesWithOmId,
  validationSchema,
} from './form/constants';
import AbsencesFooter from './parts/AbsencesFooter';
import { type ContactType, type FormValues, type SubmitValues } from './types';

const AbsencesEditPage: React.FC = () => {
  const [isConfirming, setIsConfirming] = React.useState<boolean>(false);
  const [formValues, setFormValues] = React.useState<SubmitValues | null>(null);

  const { absenceId = '', contactType = '' } = useParams();

  const { fetch, isFetching, status, data, error } =
    useFetch<AbsenceDetailPayload>(
      'GET',
      `/api/school_communication/absences/${absenceId}/${contactType}`,
    );

  const [currentUser] = useCurrentUser();

  const student = (currentUser?.organization_memberships || []).find(
    ({ id }) => id === data?.absence.organization_membership_id,
  );

  const {
    registerWithInnerRef,
    formState: { validFields, dirtyFields, errorMessages, isValid },
    handleSubmit,
    clearErrors,
  } = useReactHookForm<FormValues>({
    validationSchema: validationSchema(),
    defaultValues: {
      'absence.date': data?.absence.date,
      contact_type: data?.absence_log.contact_type,
      reason: data?.absence_log.reason,
      go_to_school_at: data?.absence_log.go_to_school_at || '',
      leave_school_at: data?.absence_log.leave_school_at || '',
      disease_name: data?.absence_log.disease_name || '',
      symptom: data?.absence_log.symptom || '',
      body_temperature: data?.absence_log.body_temperature || '',
      doctors_instruction: isBoolean(data?.absence_log.doctors_instruction)
        ? String(Number(data?.absence_log.doctors_instruction))
        : '',
      relationship: data?.absence_log.relationship || '',
      transportation: data?.absence_log.transportation || '',
      detail_comment: data?.absence_log.detail_comment || '',
    },
  });

  const { showModal } = useModal();

  const onCancel = React.useCallback(() => {
    showModal('AbsencesCancelFormModal', {
      url: `/absences/${absenceId}/type/${contactType}`,
    });
  }, [showModal, absenceId, contactType]);

  const onSubmit = React.useCallback(
    (values: FormValues) => {
      const submitValues = convertToSubmitValuesWithOmId(
        values,
        data?.absence.organization_membership_id as string,
      );

      setFormValues(submitValues);
      setIsConfirming(true);
    },
    [data?.absence.organization_membership_id],
  );

  React.useEffect(() => {
    void fetch();
  }, [fetch]);

  if (isConfirming && formValues)
    return (
      <AbsencesConfirmPage
        organizationMembershipId={
          data?.absence.organization_membership_id as string
        }
        values={formValues}
        onBack={() => {
          setIsConfirming(false);
        }}
        onCancel={onCancel}
        submitStatus="updated"
      />
    );

  if (status !== 200 && error) {
    if (Number(status) >= 500 && Number(status) <= 599) {
      return <ErrorPage type="internal_server_error" />;
    }

    if (status === 404) {
      return <ErrorPage type="not_found" />;
    }

    return <ErrorPage type="forbidden" />;
  }

  if (!student) return null;

  return (
    <SingleColumnTemplate>
      <UiStackTemplate isLoading={isFetching}>
        <PageTitle
          title="欠席連絡の編集"
          rightLinkLabel="キャンセル"
          onRightLinkClick={onCancel}
          rightLinkColor="gray"
        />
        <div className={styles.contents}>
          {/* SEE: https://github.com/react-hook-form/react-hook-form/discussions/8020 */}
          {/* eslint-disable-next-line @typescript-eslint/no-misused-promises */}
          <form onSubmit={handleSubmit(onSubmit)}>
            <Jumbotron flexHeight>
              <Paper>
                <Panel title="欠席連絡詳細">
                  <FormGroup>
                    <Field isFullWidth>
                      <div className={styles.noteText}>
                        生徒名、日付、内容は変更できません。変更したい場合はこちらの欠席連絡を取り消し、再登録してください。
                      </div>
                    </Field>
                  </FormGroup>

                  <AbsencesFormStaticInput
                    label="生徒名"
                    value={student.name}
                  />

                  <AbsencesFormStaticInput
                    label="日付"
                    value={
                      data?.absence.date &&
                      format(parseISO(data.absence.date), 'yyyy年M月d日')
                    }
                  />

                  <AbsencesFormStaticInput
                    label="内容"
                    value={contactTypeNames[contactType as ContactType]}
                  />

                  <AbsencesFormInputsOnOrAfterReason
                    registerWithInnerRef={registerWithInnerRef}
                    validFields={validFields}
                    dirtyFields={dirtyFields}
                    contactType={contactType}
                    errorMessages={errorMessages}
                    clearErrors={clearErrors}
                  />
                </Panel>
              </Paper>
              <AbsencesFooter>
                <div className={styles.buttons}>
                  <Button
                    color="primary"
                    size="l"
                    type="submit"
                    disabled={!isValid}
                    className={styles.button}
                  >
                    次へ
                  </Button>
                  <Button
                    color="link"
                    size="l"
                    onClick={onCancel}
                    className={styles.button}
                  >
                    キャンセル
                  </Button>
                </div>
              </AbsencesFooter>
            </Jumbotron>
          </form>
        </div>
      </UiStackTemplate>
    </SingleColumnTemplate>
  );
};

export default AbsencesEditPage;
